import React, { useContext } from 'react'
import { ComponentConfig, ComponentData, renderContext } from '@puckeditor/core'
import analytics from 'global/AnalyticsData'
import { ThemeCustomFields } from 'breaktime-theme/src/puck/theme-config'
import {
  BorderOverlayProps,
  TypographyProps,
  borderOverlayField,
  borderOverlayStyle,
  fontFamilyField,
  fontFamilyFallbackField,
  applyTypography,
} from 'breaktime-theme/src/puck/fields/StyleFields'

/**
 * Generic shop/CTA button used across puck steps (referral cards, PQ
 * results). Registered as `ShopButton` (canonical) and `ReferralButton`
 * (hidden legacy alias so earlier saved steps keep working). In production
 * the step containers MINE its props into the legacy runtime (link fields,
 * inline styles), the component itself renders only in the editor.
 */
export type ReferralButtonProps = {
  id?: string
  url: string
  /** Empty text = no visible button; in referral cards the url still makes
   * the card image a tracked link (legacy `showButton: false` behavior).
   * Hiding via the responsive `display` field would prune the component,
   * url included. */
  buttonText: string
  buttonColor?: string
  buttonHoverColor?: string
  textColor?: string
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  borderRadius?: string
  /** CSS padding shorthand, e.g. `12px 24px`. */
  padding?: string
  fontSize?: string
  minHeight?: string
  borderOverlay?: BorderOverlayProps
}

export type ShopButtonProps = ReferralButtonProps

/** Inline style for the shop button beyond the background vars — shared by
 * the editor render and the production mappings. */
export const referralButtonStyle = (p: {
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  borderRadius?: string
  padding?: string
  textColor?: string
  fontSize?: string
  minHeight?: string
}): React.CSSProperties => ({
  ...applyTypography({
    fontFamily: p.fontFamily,
    fontFamilyFallback: p.fontFamilyFallback,
  }),
  ...(p.borderRadius ? { borderRadius: p.borderRadius } : {}),
  ...(p.padding ? { padding: p.padding } : {}),
  ...(p.fontSize ? { fontSize: p.fontSize } : {}),
  ...(p.minHeight ? { minHeight: p.minHeight } : {}),
  color: p.textColor || '#fff',
})

/** CSS custom props consumed by referral-puck.scss (hover needs a stylesheet —
 * an inline background would always beat the :hover rule). */
export const referralButtonVars = (p: {
  buttonColor?: string
  buttonHoverColor?: string
}): React.CSSProperties =>
  ({
    ...(p.buttonColor ? { '--referral-button-bg': p.buttonColor } : {}),
    ...(p.buttonHoverColor
      ? { '--referral-button-hover-bg': p.buttonHoverColor }
      : {}),
  } as React.CSSProperties)

/** Full mined style bundle (vars + inline) for legacy-rendered shop buttons. */
export const shopButtonStyle = (p: ShopButtonProps): React.CSSProperties => ({
  ...referralButtonVars(p),
  ...referralButtonStyle(p),
  // the border overlay child positions against the button
  ...(borderOverlayStyle(p.borderOverlay)
    ? { position: 'relative', overflow: 'visible' }
    : {}),
})

/** Overlay child style for legacy-rendered shop buttons (undefined = none). */
export const shopButtonOverlay = (
  p: ShopButtonProps,
): React.CSSProperties | undefined => borderOverlayStyle(p.borderOverlay)

/** First ShopButton (or legacy ReferralButton) in a slot. */
export const getShopButton = (
  items?: ComponentData[],
): { type: string; props: ShopButtonProps } | undefined =>
  (items as { type: string; props: ShopButtonProps }[] | undefined)?.find(
    a => a.type === 'ShopButton' || a.type === 'ReferralButton',
  )

export const REFERRAL_BUTTON_DEFAULT_PROPS: ReferralButtonProps = {
  url: 'https://',
  buttonText: 'Shop Now',
  // no explicit color: falls back to the break's theme button color
  buttonColor: undefined,
}

export const PuckReferralButton: ComponentConfig<{
  props: ReferralButtonProps
  fields: ThemeCustomFields
}> = {
  label: 'Shop Button',
  defaultProps: REFERRAL_BUTTON_DEFAULT_PROPS,
  fields: {
    url: { type: 'text', label: 'Link URL' },
    buttonText: {
      type: 'text',
      label: 'Button Text (empty = hidden / image-only link)',
    },
    buttonColor: { type: 'colorPicker', label: 'Button Color', clearable: true },
    buttonHoverColor: {
      type: 'colorPicker',
      label: 'Button Hover Color',
      clearable: true,
    },
    textColor: { type: 'colorPicker', label: 'Text Color', clearable: true },
    fontFamily: fontFamilyField,
    fontFamilyFallback: fontFamilyFallbackField,
    borderRadius: {
      type: 'text',
      label: 'Border Radius',
      placeholder: '5px',
    },
    padding: {
      type: 'text',
      label: 'Padding',
      placeholder: '15px',
    },
    fontSize: { type: 'text', label: 'Font Size', placeholder: '16px' },
    minHeight: { type: 'text', label: 'Min Height', placeholder: '45px' },
    borderOverlay: borderOverlayField,
  },
  render: props => {
    const { url, buttonText, puck } = props
    // Node-rendered contexts (PQ result buttons row etc): fire step-named
    // click analytics + engage/points. Mined contexts (referral cards)
    // never render this component in production, the legacy handlers own it.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const puckCtx = useContext(renderContext)
    const meta = (puckCtx?.metadata ?? {}) as {
      step?: { type?: string }
      shareEventSlug?: string
      onEngage?: () => void
      onAwardPoints?: () => void
    }
    if (!buttonText?.trim()) return <></>
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        // shop-btn (referral scss) / shop-button (quiz scss): whichever step
        // context is loaded styles the base; puck-shop-btn drives bg + hover
        className="shop-btn shop-button puck-shop-btn"
        style={shopButtonStyle(props)}
        onClick={e => {
          // in the editor don't navigate
          if (puck?.isEditing) {
            e.preventDefault()
            return
          }
          if (meta.step && meta.shareEventSlug) {
            analytics.stepEvent(
              `step_interaction_${meta.shareEventSlug}_button_click`,
              meta.step,
              { result_id: props.id, result: `${buttonText} | ${url}` },
            )
          }
          meta.onEngage?.()
          meta.onAwardPoints?.()
        }}
      >
        {shopButtonOverlay(props) && (
          <span aria-hidden style={shopButtonOverlay(props)} />
        )}
        {buttonText}
      </a>
    )
  },
}
